import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { powtoonBE } from "@/lib/powtoon-be/client";
import type { Share, ShareUserExtra } from "@/lib/powtoon-be/types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { sharePermission, COLLEAGUE_EDIT_ENABLED, type SharePermission } from "@/lib/scenarios/editAccess";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ShareScenarioModal({
  scenarioId,
  open,
  onOpenChange,
}: {
  scenarioId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [invitePerm, setInvitePerm] = useState<SharePermission>("view");
  const [pendingAdds, setPendingAdds] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);

  const sharesQuery = useQuery({
    queryKey: ["scenario-shares", scenarioId],
    queryFn: () => powtoonBE.scenarios.listShares(scenarioId),
    enabled: open,
  });

  const groupsQuery = useQuery({
    queryKey: ["user", "groups"],
    queryFn: () => powtoonBE.user.groups(),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const invalidateShares = () =>
    qc.invalidateQueries({ queryKey: ["scenario-shares", scenarioId] });

  type ShareVars = { addr: string; perm: SharePermission };
  const inviteMutation = useMutation({
    mutationFn: ({ addr, perm }: ShareVars) =>
      powtoonBE.scenarios.shareWithEmail(scenarioId, addr, perm === "edit"),
    onSuccess: async () => {
      setEmail("");
      await invalidateShares();
      toast.success("Invitation sent");
    },
    onError: (err: Error) => toast.error(err.message || "Failed to send invite"),
  });

  // Changing a colleague's permission re-shares by email with the new set.
  const permissionMutation = useMutation({
    mutationFn: ({ addr, perm }: ShareVars) =>
      powtoonBE.scenarios.shareWithEmail(scenarioId, addr, perm === "edit"),
    onSuccess: async () => {
      await invalidateShares();
      toast.success("Permission updated");
    },
    onError: (err: Error) => toast.error(err.message || "Failed to update permission"),
  });

  const applyMutation = useMutation({
    mutationFn: async ({ adds }: { adds: string[] }) => {
      const results = await Promise.allSettled(
        adds.map((id) => powtoonBE.scenarios.shareWithGroup(scenarioId, id)),
      );
      const total = adds.length;
      const failed = results.filter((r) => r.status === "rejected").length;
      return { total, failed };
    },
    onSuccess: async ({ total, failed }) => {
      await invalidateShares();
      setPendingAdds(new Set());
      if (failed === 0) {
        toast.success("Team access updated");
      } else if (failed === total) {
        toast.error("Failed to update team access");
      } else {
        toast.warning(`${total - failed} updated, ${failed} failed`);
      }
    },
    onError: (err: Error) =>
      toast.error(err.message || "Failed to update team access"),
  });

  const shares: Share[] = sharesQuery.data ?? [];
  const rows = shares.filter((s) => s.grantee_type !== "organization");
  const userShares = rows.filter(
    (s) => s.grantee_type === "user" || s.grantee_type === "lead",
  );
  const groupShareByGroupId = new Map<string, Share>();
  for (const s of rows) {
    if (s.grantee_type === "group") groupShareByGroupId.set(s.grantee_id, s);
  }

  const userLabel = (extra: ShareUserExtra, fallback: string) => {
    if (extra.email) return extra.email;
    const full = [extra.first_name, extra.last_name].filter(Boolean).join(" ");
    return full || fallback;
  };

  const emailValid = EMAIL_RE.test(email.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Share your live avatar</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="invite" className="mt-2">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="invite">Share with a colleague</TabsTrigger>
            <TabsTrigger value="team">Share with a team</TabsTrigger>
          </TabsList>

          <TabsContent value="invite" className="mt-4 space-y-4 min-h-[380px]">
            <p className="text-sm text-muted-foreground">
              Share this live avatar with colleagues in your company.
              {COLLEAGUE_EDIT_ENABLED && ' Choose "Can edit" to let them change it too.'}
            </p>
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (emailValid && !inviteMutation.isPending) {
                  inviteMutation.mutate({
                    addr: email.trim(),
                    perm: COLLEAGUE_EDIT_ENABLED ? invitePerm : "view",
                  });
                }
              }}
            >
              <Input
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1"
              />
              {COLLEAGUE_EDIT_ENABLED && (
              <Select value={invitePerm} onValueChange={(v) => setInvitePerm(v as SharePermission)}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="view">Can view</SelectItem>
                  <SelectItem value="edit">Can edit</SelectItem>
                </SelectContent>
              </Select>
              )}
              <Button
                type="submit"
                disabled={!emailValid || inviteMutation.isPending}
              >
                {inviteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Invite"
                )}
              </Button>
            </form>

            <div>
              <h4 className="mb-2 text-sm font-medium text-muted-foreground">
                Shared with
              </h4>
              {sharesQuery.isLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : userShares.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Not shared with anyone yet.
                </p>
              ) : (
                <ul className="divide-y rounded-md border">
                  {userShares.map((s) => {
                    const extra = (s.extra_data ?? {}) as ShareUserExtra;
                    const label = userLabel(extra, s.grantee_id);
                    return (
                      <li
                        key={s.id}
                        className="flex items-center justify-between px-3 py-2"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {extra.thumb_url ? (
                            <img
                              src={extra.thumb_url}
                              alt=""
                              className="h-8 w-8 shrink-0 rounded-full bg-muted object-cover"
                            />
                          ) : (
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                              {label.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span className="truncate text-sm">{label}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {s.grantee_type === "lead" ? (
                            <span className="text-xs text-muted-foreground">
                              Pending invite
                            </span>
                          ) : null}
                          {COLLEAGUE_EDIT_ENABLED && (
                          <Select
                            value={sharePermission(s)}
                            disabled={!extra.email || permissionMutation.isPending}
                            onValueChange={(v) =>
                              extra.email &&
                              permissionMutation.mutate({ addr: extra.email, perm: v as SharePermission })
                            }
                          >
                            <SelectTrigger className="h-8 w-[110px] text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="view">Can view</SelectItem>
                              <SelectItem value="edit">Can edit</SelectItem>
                            </SelectContent>
                          </Select>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </TabsContent>

          <TabsContent value="team" className="mt-4 space-y-3 min-h-[380px]">
            <h4 className="text-sm font-medium">All (Teams)</h4>
            {groupsQuery.isLoading || sharesQuery.isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : (groupsQuery.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                You don't belong to any teams yet.
              </p>
            ) : (
                <ul className="divide-y rounded-md border">
                  {(groupsQuery.data ?? []).map((g) => {
                    const existingShare = groupShareByGroupId.get(g.id);
                    const isShared = !!existingShare;
                    const queuedAdd = pendingAdds.has(g.id);
                    const checked = isShared || queuedAdd;
                    return (
                      <li
                        key={g.id}
                        className="flex items-center justify-between px-3 py-3"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="truncate text-sm">{g.title}</span>
                        </div>
                        <Switch
                          checked={checked}
                          disabled={isShared || applyMutation.isPending}
                          onCheckedChange={(v) => {
                            if (isShared) return;
                            setPendingAdds((prev) => {
                              const next = new Set(prev);
                              if (v) next.add(g.id);
                              else next.delete(g.id);
                              return next;
                            });
                          }}
                        />
                      </li>
                    );
                  })}
                </ul>
            )}
            <div className="flex justify-end pt-2">
              <Button
                disabled={pendingAdds.size === 0 || applyMutation.isPending}
                onClick={() => setConfirmOpen(true)}
              >
                {applyMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Apply"
                )}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Share with selected teams?</AlertDialogTitle>
            <AlertDialogDescription>
              Sharing can't be undone from here yet — once a team gets access,
              you won't be able to remove it from this screen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                applyMutation.mutate({ adds: Array.from(pendingAdds) })
              }
            >
              Share
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}